import React, { useState, useEffect, useContext } from "react";
import { ServerConfigContext } from "../context/ServerConfigContext";
import { createApiService } from "../utils/apiService";
import { transformApiData, getRateColorClass } from "../utils/formatUtils";
import TradingTable from "../components/TradingTable";
import "./TradingPages.css";

// 쿠키 유틸리티 함수
const setCookie = (name, value, days = 30) => {
  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const cookieValue = encodeURIComponent(JSON.stringify(value));
    document.cookie = `${name}=${cookieValue};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  } catch (error) {
    console.error("쿠키 저장 오류:", error);
  }
};

const getCookie = (name) => {
  try {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const cookieValue = c.substring(nameEQ.length, c.length);
        const decodedValue = decodeURIComponent(cookieValue);
        return JSON.parse(decodedValue);
      }
    }
    return null;
  } catch (error) {
    console.error("쿠키 불러오기 오류:", error);
    return null;
  }
};

const ThemePage = () => {
  const { getServer, tableSettings, filterStockData, isConfigLoaded } =
    useContext(ServerConfigContext);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedTime, setSelectedTime] = useState(
    new Date().toTimeString().split(" ")[0].substring(0, 5)
  );
  const [timeUnit, setTimeUnit] = useState("분"); // '분', '시', '일'
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tradingData, setTradingData] = useState([]);
  const [themeRankingData, setThemeRankingData] = useState([]); // 테마 랭킹 데이터
  const [allAvailableThemes, setAllAvailableThemes] = useState([]); // 현재 표시된 모든 테마
  const [selectedThemes, setSelectedThemes] = useState(new Set()); // 선택된 테마들
  const [currentThemePage, setCurrentThemePage] = useState(0); // 테마 랭킹 페이지 (0: 1-5위, 1: 6-10위)
  const [isSearching, setIsSearching] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showThemeFilterSettings, setShowThemeFilterSettings] = useState(false);
  const [newIgnoreKeyword, setNewIgnoreKeyword] = useState("");
  const [visibleDetails, setVisibleDetails] = useState({
    sector: true,
    per: true,
    pbr: true,
    foreignBuy: true,
    institutionBuy: true,
    individualBuy: true,
    high52Week: true,
    low52Week: true,
    volume52Week: true,
  });

  // 무시할 테마 키워드 (쿠키에서 불러오기)
  const [ignoreThemeKeywords, setIgnoreThemeKeywords] = useState(() => {
    const saved = getCookie("themePageIgnoreKeywords");
    return saved || [];
  });

  // ignoreThemeKeywords 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie("themePageIgnoreKeywords", ignoreThemeKeywords);
  }, [ignoreThemeKeywords]);

  // 기본 차트 설정
  const defaultChartSettings = {
    upColor: "#d32f2f",
    downColor: "#1976d2",
    wickColor: "#424242",
    wickThickness: 1,
    bodyThickness: 0.8,
    showMA5: true,
    showMA10: true,
    showMA20: true,
    showMA60: false,
    showMA120: false,
    ma5Color: "#ff9800",
    ma10Color: "#4caf50",
    ma20Color: "#9c27b0",
    ma60Color: "#f44336",
    ma120Color: "#795548",
    showGrid: true,
    gridColor: "#e0e0e0",
  };

  // 쿠키에서 차트 설정 불러오기
  const [chartSettings, setChartSettings] = useState(() => {
    const savedSettings = getCookie("themePageChartSettings");
    return savedSettings
      ? { ...defaultChartSettings, ...savedSettings }
      : defaultChartSettings;
  });

  const [activeTab, setActiveTab] = useState("chart");

  // 종목정보 전체 표시 여부 상태 (PressedPage와 동일한 쿠키 사용)
  const [showDetailsSection, setShowDetailsSection] = useState(() => {
    const savedShowDetails = getCookie("pressedPageShowDetailsSection");
    return savedShowDetails !== undefined ? savedShowDetails : true;
  });

  // setShowDetailsSection 래퍼 함수
  const handleSetShowDetailsSection = (newValue) => {
    setShowDetailsSection(newValue);
  };

  // showDetailsSection 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie("pressedPageShowDetailsSection", showDetailsSection);
  }, [showDetailsSection]);

  // chartSettings 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie("themePageChartSettings", chartSettings);
  }, [chartSettings]);

  // 선택된 테마가 변경될 때마다 종목 데이터 필터링
  const [rawTradingData, setRawTradingData] = useState([]); // 원본 데이터 보관

  useEffect(() => {
    if (rawTradingData.length > 0) {
      const filteredBySelectedThemes =
        filterStocksBySelectedThemes(rawTradingData);

      // rank 기준 오름차순 정렬 (원본 배열을 변경하지 않도록 복사 후 정렬)
      const sortedData = [...filteredBySelectedThemes].sort((a, b) => {
        const rankA = parseInt(a.rank) || 0;
        const rankB = parseInt(b.rank) || 0;
        return rankA - rankB;
      });

      setTradingData(sortedData);
      console.log(
        "선택된 테마 기반 필터링 완료:",
        selectedThemes.size,
        "개 테마 선택됨"
      );
    } else {
      setTradingData([]); // rawTradingData가 비어있으면 tradingData도 초기화
    }
  }, [selectedThemes, rawTradingData]);

  // 서버에서 조회 가능한 날짜 목록을 가져오는 함수
  const fetchAvailableDates = async () => {
    try {
      console.log("📅 [ThemePage] 날짜 목록 조회 시작...");
      setLoading(true);
      const server = getServer("stock-api-server");

      if (!server) {
        console.error("❌ stock-api-server를 찾을 수 없습니다.");
        setIsInitializing(false);
        return;
      }

      if (!server.isActive) {
        console.error("❌ stock-api-server가 비활성 상태입니다.");
        setIsInitializing(false);
        return;
      }

      console.log("🔗 API 서버:", server.baseUrl);
      const apiService = createApiService(server);
      console.log("🚀 /api/v1/ranks API 호출 중...");
      const dateList = await apiService.get("/api/v1/ranks");
      console.log("✅ 날짜 목록 조회 성공:", dateList);

      setAvailableDates(dateList);

      // 현재 선택된 날짜가 조회 가능한 날짜 목록에 없으면 가장 최근 날짜로 설정
      if (dateList.length > 0) {
        const currentSelectedDateFormatted = selectedDate.replace(/-/g, "");
        if (!dateList.includes(currentSelectedDateFormatted)) {
          const latestDate = dateList.sort().reverse()[0]; // 가장 최근 날짜
          const formattedDate = `${latestDate.substring(
            0,
            4
          )}-${latestDate.substring(4, 6)}-${latestDate.substring(6, 8)}`;
          console.log(
            `📅 [ThemePage] 가장 최근 날짜로 자동 설정: ${formattedDate}`
          );
          setSelectedDate(formattedDate);
        }
      }
    } catch (error) {
      console.error("❌ 조회 가능한 날짜를 가져오는 중 오류 발생:", error);
    } finally {
      setLoading(false);
      setIsInitializing(false);
    }
  };

  // 선택된 날짜가 조회 가능한 날짜인지 확인하는 함수
  const isDateAvailable = (date) => {
    const formattedDate = date.replace(/-/g, "");
    return availableDates.includes(formattedDate);
  };

  // 날짜 변경 핸들러 (조회 가능한 날짜만 허용)
  const handleDateChange = (newDate) => {
    if (isDateAvailable(newDate)) {
      setSelectedDate(newDate);
    }
  };

  // 시간이 유효한 범위인지 확인 (09:00 ~ 15:30)
  const isTimeInValidRange = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    const timeInMinutes = hours * 60 + minutes;
    const startTime = 9 * 60; // 09:00
    const endTime = 15 * 60 + 30; // 15:30

    return timeInMinutes >= startTime && timeInMinutes <= endTime;
  };

  // 선택된 날짜가 오늘인지 확인 (현지 시간 기준)
  const isSelectedDateToday = (dateString) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    const today = `${year}-${month}-${day}`;

    return dateString === today;
  };

  // 현재 시간보다 이후인지 확인 (오늘 날짜일 때)
  const isTimeAfterNow = (timeString) => {
    const now = new Date();
    const [hours, minutes] = timeString.split(":").map(Number);
    const selectedTime = new Date();
    selectedTime.setHours(hours, minutes, 0, 0);

    return selectedTime > now;
  };

  // 시간 유효성 검사 (날짜에 종속)
  const isTimeValid = (dateString, timeString) => {
    // 1단계: 기본 거래 시간 범위 검사 (09:00~15:30)
    if (!isTimeInValidRange(timeString)) {
      return false;
    }

    // 2단계: 날짜에 따른 시간 제한 검사
    if (isSelectedDateToday(dateString)) {
      // 오늘 날짜: 09:00 ~ 현재 시각까지만 선택 가능
      if (isTimeAfterNow(timeString)) {
        return false;
      }
    }
    // 과거 날짜: 09:00 ~ 15:30 모든 시간 선택 가능

    return true;
  };

  // 시간 변경 핸들러 - 유효성 검사 없이 바로 상태 반영
  const handleTimeChange = (newTime) => {
    setSelectedTime(newTime);
  };

  // 서버 설정이 로드된 후에 데이터 초기화
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 5;
    const retryInterval = 300;

    const initializeData = async () => {
      try {
        console.log("🚀 ThemePage 초기화 시작...", {
          isConfigLoaded,
          retryCount,
        });

        // 서버 설정이 실제로 사용 가능한지 확인
        const server = getServer("stock-api-server");
        if (server && server.isActive) {
          console.log("✅ ThemePage - 서버 설정 확인됨, 데이터 초기화 시작");
          await fetchAvailableDates();
          return;
        }

        // 서버 설정이 없거나 비활성 상태일 때 재시도
        if (retryCount < maxRetries) {
          retryCount++;
          console.warn(
            `⚠️ ThemePage - 서버 설정 대기 중 (${retryCount}/${maxRetries})...`
          );
          setTimeout(initializeData, retryInterval);
        } else {
          console.error("❌ ThemePage - 서버 설정 초기화 최대 재시도 실패");
          setIsInitializing(false);
        }
      } catch (error) {
        console.error("❌ ThemePage - 초기화 중 오류:", error);
        setIsInitializing(false);
      }
    };

    // isConfigLoaded가 true가 되거나 일정 시간 후 시작
    if (isConfigLoaded) {
      initializeData();
    } else {
      // 설정 로드를 기다리지만 최대 2초 후에는 강제 시작
      const forceInitTimeout = setTimeout(() => {
        console.warn(
          "⚠️ ThemePage - 설정 로드 대기 시간 초과, 강제 초기화 시작"
        );
        initializeData();
      }, 2000);

      // isConfigLoaded가 true가 되면 타이머 정리
      if (isConfigLoaded) {
        clearTimeout(forceInitTimeout);
      }

      return () => clearTimeout(forceInitTimeout);
    }
  }, [isConfigLoaded, getServer]);

  // availableDates가 업데이트되면 초기 데이터 로드
  useEffect(() => {
    if (
      availableDates.length > 0 &&
      isDateAvailable(selectedDate) &&
      isTimeValid(selectedDate, selectedTime)
    ) {
      fetchTradingData(selectedDate, selectedTime).finally(() => {
        setIsInitializing(false);
      });
    } else if (availableDates.length > 0) {
      // 날짜는 로드되었지만 조건에 맞지 않는 경우에도 초기화 완료
      setIsInitializing(false);
    }
  }, [availableDates]);

  // 초기 시간 설정 (09:00 ~ 15:30 범위 내에서)
  useEffect(() => {
    if (!isTimeInValidRange(selectedTime)) {
      setSelectedTime("09:00");
    }
  }, []);

  // 날짜가 변경될 때 시간 조정
  useEffect(() => {
    if (isSelectedDateToday(selectedDate)) {
      // 오늘 날짜인 경우: 현재 시각 이후면 조정
      if (isTimeAfterNow(selectedTime)) {
        const now = new Date();
        const currentHour = now.getHours().toString().padStart(2, "0");
        const currentMinute = now.getMinutes().toString().padStart(2, "0");
        const currentTimeString = `${currentHour}:${currentMinute}`;

        if (isTimeInValidRange(currentTimeString)) {
          setSelectedTime(currentTimeString);
        } else if (now.getHours() < 9) {
          setSelectedTime("09:00");
        } else {
          setSelectedTime("15:30");
        }
      }
    } else {
      // 과거 날짜인 경우: 15:20으로 설정
      const selectedDateObj = new Date(selectedDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDateObj.setHours(0, 0, 0, 0);

      if (selectedDateObj < today) {
        setSelectedTime("15:20");
      }
    }
  }, [selectedDate]);

  const handlePrevious = () => {
    const [hours, minutes] = selectedTime.split(":").map(Number);
    const currentDate = new Date(selectedDate);
    let shouldFetch = false;
    let newDate = selectedDate;
    let newTime = selectedTime;

    switch (timeUnit) {
      case "분":
        // 분만 변경, 날짜와 시간은 고정
        let newMinutes = minutes - 1;
        let newHours = hours;

        if (newMinutes < 0) {
          newMinutes = 59;
          newHours = hours - 1;
        }

        const newTimeString = `${newHours
          .toString()
          .padStart(2, "0")}:${newMinutes.toString().padStart(2, "0")}`;

        // 시간 제한 확인
        if (isTimeValid(selectedDate, newTimeString)) {
          setSelectedTime(newTimeString);
          newTime = newTimeString;
          shouldFetch = true;
        }
        break;
      case "시":
        // 시간만 변경, 날짜는 고정
        const newHours_h = hours - 1;
        const newTimeString_h = `${newHours_h
          .toString()
          .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;

        // 시간 제한 확인
        if (newHours_h >= 0 && isTimeValid(selectedDate, newTimeString_h)) {
          setSelectedTime(newTimeString_h);
          newTime = newTimeString_h;
          shouldFetch = true;
        }
        break;
      case "일":
        // 날짜만 변경, 시간은 그대로
        const prevDate = new Date(currentDate);
        prevDate.setDate(prevDate.getDate() - 1);
        const prevDateString = prevDate.toISOString().split("T")[0];
        setSelectedDate(prevDateString);
        newDate = prevDateString;
        shouldFetch = true;
        break;
    }

    // 값이 변경되었으면 자동으로 조회
    if (shouldFetch) {
      // 약간의 지연을 두어 상태 업데이트가 완료된 후 조회
      setTimeout(() => {
        fetchTradingData(newDate, newTime);
      }, 50);
    }
  };

  const handleNext = () => {
    const [hours, minutes] = selectedTime.split(":").map(Number);
    const currentDate = new Date(selectedDate);
    let shouldFetch = false;
    let newDate = selectedDate;
    let newTime = selectedTime;

    switch (timeUnit) {
      case "분":
        // 분만 변경, 날짜와 시간은 고정
        let newMinutes_n = minutes + 1;
        let newHours_n = hours;

        if (newMinutes_n >= 60) {
          newMinutes_n = 0;
          newHours_n = hours + 1;
        }

        const newTimeString_n = `${newHours_n
          .toString()
          .padStart(2, "0")}:${newMinutes_n.toString().padStart(2, "0")}`;

        // 시간 제한 확인
        if (newHours_n < 24 && isTimeValid(selectedDate, newTimeString_n)) {
          setSelectedTime(newTimeString_n);
          newTime = newTimeString_n;
          shouldFetch = true;
        }
        break;
      case "시":
        // 시간만 변경, 날짜는 고정
        const newHours_nh = hours + 1;
        const newTimeString_nh = `${newHours_nh
          .toString()
          .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;

        // 시간 제한 확인
        if (newHours_nh < 24 && isTimeValid(selectedDate, newTimeString_nh)) {
          setSelectedTime(newTimeString_nh);
          newTime = newTimeString_nh;
          shouldFetch = true;
        }
        break;
      case "일":
        // 날짜만 변경, 시간은 그대로
        const nextDate = new Date(currentDate);
        nextDate.setDate(nextDate.getDate() + 1);
        const nextDateString = nextDate.toISOString().split("T")[0];
        setSelectedDate(nextDateString);
        newDate = nextDateString;
        shouldFetch = true;
        break;
    }

    // 값이 변경되었으면 자동으로 조회
    if (shouldFetch) {
      // 약간의 지연을 두어 상태 업데이트가 완료된 후 조회
      setTimeout(() => {
        fetchTradingData(newDate, newTime);
      }, 50);
    }
  };

  const handleSearch = () => {
    // 유효성 검사 - 조회 버튼 클릭 시에만 실행
    if (!isTimeInValidRange(selectedTime)) {
      alert("거래 시간(09:00 ~ 15:30) 범위 내의 시간만 선택할 수 있습니다.");
      return;
    }

    if (isSelectedDateToday(selectedDate) && isTimeAfterNow(selectedTime)) {
      const now = new Date();
      const currentHour = now.getHours().toString().padStart(2, "0");
      const currentMinute = now.getMinutes().toString().padStart(2, "0");
      alert(
        `현재 시간(${currentHour}:${currentMinute})까지만 선택할 수 있습니다.\n미래 시간은 선택할 수 없습니다.`
      );
      return;
    }

    console.log("조회 요청:", { selectedDate, selectedTime, timeUnit });
    // API 호출
    fetchTradingData(selectedDate, selectedTime);
  };

  // 테마가 필터 조건에 맞는지 확인하는 함수
  const shouldIncludeTheme = (themeName) => {
    // 1. 무시할 테마 키워드 체크 (대소문자 구분 없음)
    if (ignoreThemeKeywords.length > 0) {
      const lowerThemeName = themeName.toLowerCase();
      const hasIgnoredKeyword = ignoreThemeKeywords.some((keyword) =>
        lowerThemeName.includes(keyword.toLowerCase())
      );
      if (hasIgnoredKeyword) {
        return false; // 무시할 키워드가 포함되면 제외
      }
    }

    // 2. 기존 전역 테마 필터 체크
    if (
      !tableSettings.themeFilter.enabled ||
      !tableSettings.themeFilter.keywords.length
    ) {
      return true; // 필터가 비활성화되어 있거나 키워드가 없으면 모든 테마 포함
    }

    const { keywords, mode, caseSensitive } = tableSettings.themeFilter;
    const searchName = caseSensitive ? themeName : themeName.toLowerCase();

    const hasMatchingKeyword = keywords.some((keyword) => {
      const searchKeyword = caseSensitive ? keyword : keyword.toLowerCase();
      return searchName.includes(searchKeyword);
    });

    // mode가 'exclude'이면 매칭되지 않는 것들을 포함, 'include'이면 매칭되는 것들만 포함
    return mode === "exclude" ? !hasMatchingKeyword : hasMatchingKeyword;
  };

  // 상위 15개 테마를 거래대금 순으로 추출하는 함수
  const extractTop15Themes = (data) => {
    const themeMap = new Map();

    // 각 테마별 거래대금 합산 계산
    data.forEach((item) => {
      if (item.themes && Array.isArray(item.themes)) {
        item.themes.forEach((theme) => {
          const themeName = theme?.trim();
          if (themeName && shouldIncludeTheme(themeName)) {
            const tradingValueRaw = item.tradingValueRaw || 0;
            const tradingValue = tradingValueRaw * 1000000; // API 원본값에 100만원만 곱하기

            if (!themeMap.has(themeName)) {
              themeMap.set(themeName, 0);
            }
            themeMap.set(themeName, themeMap.get(themeName) + tradingValue);
          }
        });
      }
    });

    // 거래대금 순으로 정렬하여 상위 15개 반환 (중복 제거를 위해 Set 사용)
    const uniqueThemes = Array.from(
      new Set(
        Array.from(themeMap.entries())
          .sort((a, b) => b[1] - a[1]) // 거래대금 내림차순 정렬
          .slice(0, 15) // 상위 15개만
          .map(([themeName]) => themeName) // 테마명만 추출
      )
    );

    return uniqueThemes;
  };

  // 테마 선택/해제 핸들러
  const handleThemeToggle = (themeName) => {
    const newSelectedThemes = new Set(selectedThemes);
    if (newSelectedThemes.has(themeName)) {
      newSelectedThemes.delete(themeName);
    } else {
      newSelectedThemes.add(themeName);
    }
    setSelectedThemes(newSelectedThemes);
  };

  // 전체 선택/해제 핸들러
  const handleSelectAllThemes = () => {
    if (selectedThemes.size === allAvailableThemes.length) {
      // 모든 테마가 선택되어 있으면 전체 해제
      setSelectedThemes(new Set());
    } else {
      // 일부 또는 아무것도 선택되지 않았으면 전체 선택
      setSelectedThemes(new Set(allAvailableThemes));
    }
  };

  // 무시할 키워드 추가 핸들러
  const handleAddIgnoreKeyword = () => {
    const keyword = newIgnoreKeyword.trim();
    if (keyword && !ignoreThemeKeywords.includes(keyword)) {
      setIgnoreThemeKeywords([...ignoreThemeKeywords, keyword]);
      setNewIgnoreKeyword("");
    }
  };

  // 무시할 키워드 삭제 핸들러
  const handleRemoveIgnoreKeyword = (keyword) => {
    setIgnoreThemeKeywords(ignoreThemeKeywords.filter((k) => k !== keyword));
  };

  // 상위 5개 테마 테이블에서 테마 클릭 핸들러
  const handleThemeRankingClick = (themeName) => {
    handleThemeToggle(themeName);
  };

  // 테마별 거래대금 합산 계산 함수
  const calculateThemeRanking = (data) => {
    const themeMap = new Map();

    data.forEach((item) => {
      if (item.themes && Array.isArray(item.themes)) {
        item.themes.forEach((theme) => {
          if (!theme || theme.trim() === "") return;

          const themeName = theme.trim();

          // 테마 필터링 확인
          if (!shouldIncludeTheme(themeName)) {
            console.log(`테마 "${themeName}" 필터링으로 제외됨`);
            return; // 이 테마는 필터링으로 제외
          }

          // tradingValueRaw는 API 원본값 (100만원을 곱해서 원 단위로 변환)
          const tradingValueRaw = item.tradingValueRaw || 0;
          const tradingValue = tradingValueRaw * 1000000; // API 원본값에 100만원만 곱하기
          const rateValue = item.rateValue || 0;
          const price = item.priceValue || 0;

          console.log(
            `테마 ${themeName}: 거래대금 API원본값=${tradingValueRaw} = ${tradingValue}원 = ${
              tradingValue / 100000000
            }억원`
          );

          if (!themeMap.has(themeName)) {
            themeMap.set(themeName, {
              name: themeName,
              totalTradingValue: 0,
              stockCount: 0,
              totalRate: 0,
              maxRate: -Infinity,
              leaderStock: "",
              leaderRate: 0,
              leaderPrice: 0,
            });
          }

          const themeData = themeMap.get(themeName);
          themeData.totalTradingValue += tradingValue;
          themeData.stockCount += 1;
          themeData.totalRate += rateValue;

          // 대장주 계산 (등락률이 가장 높은 종목)
          if (rateValue > themeData.maxRate) {
            themeData.maxRate = rateValue;
            themeData.leaderStock = item.name;
            themeData.leaderRate = rateValue;
            themeData.leaderPrice = price;
          }
        });
      }
    });

    // 거래대금 순으로 정렬하여 상위 10개 반환
    const result = Array.from(themeMap.values())
      .filter((theme) => theme.stockCount >= 3)
      .sort((a, b) => b.totalTradingValue - a.totalTradingValue)
      .slice(0, 10);

    console.log("테마 랭킹 결과 (필터링 적용):", result);
    return result;
  };

  // 선택된 테마에 속한 종목만 필터링하는 함수
  const filterStocksBySelectedThemes = (data) => {
    if (selectedThemes.size === 0) {
      return []; // 선택된 테마가 없으면 아무것도 표시하지 않음
    }

    return data.filter((item) => {
      if (!item.themes || !Array.isArray(item.themes)) return false;

      // 종목의 테마 중에서 선택된 테마가 있는지 확인
      const hasSelectedTheme = item.themes.some((theme) => {
        const themeName = theme?.trim();
        return (
          themeName &&
          shouldIncludeTheme(themeName) &&
          selectedThemes.has(themeName)
        );
      });

      return hasSelectedTheme;
    });
  };

  // 거래대금 데이터 조회 함수
  const fetchTradingData = async (date, time) => {
    try {
      setIsSearching(true);
      setIsInitializing(false); // 데이터 조회 시작하면 초기화 완료
      // 이전 데이터 유지 - 깜빡임 방지

      const server = getServer("stock-api-server");

      if (!server) {
        console.error("stock-api-server를 찾을 수 없습니다.");
        setRawTradingData([]); // 서버 없으면 표 비우기
        setTradingData([]);
        return;
      }

      if (!server.isActive) {
        console.error("stock-api-server가 비활성 상태입니다.");
        setRawTradingData([]); // 서버 비활성이면 표 비우기
        setTradingData([]);
        return;
      }

      const apiService = createApiService(server);

      // 날짜를 yyyyMMdd 형태로 변환
      const formattedDate = date.replace(/-/g, "");

      // 시간을 HHmm 형태로 변환
      const formattedTime = time.replace(":", "");

      // API 호출
      const response = await apiService.get(`/api/v1/ranks/${formattedDate}`, {
        time: formattedTime,
        offset: 0,
        limit: 100,
      });

      console.log("거래대금 데이터 조회 성공:", response);
      const rawData = response.data || response;

      // 데이터가 없거나 빈 배열이면 표 비우기
      if (!rawData || (Array.isArray(rawData) && rawData.length === 0)) {
        console.log("조회 결과 데이터 없음 - 표 비우기");
        setRawTradingData([]);
        setTradingData([]);
        setAllAvailableThemes([]);
        setThemeRankingData([]);
        return;
      }

      const transformedData = Array.isArray(rawData)
        ? rawData.map(transformApiData)
        : [];
      const filteredData = filterStockData(transformedData);

      // 상위 15개 테마 추출 (거래대금 순)
      const top15Themes = extractTop15Themes(filteredData);
      setAllAvailableThemes(top15Themes);

      // 테마 랭킹 계산
      const themeRanking = calculateThemeRanking(filteredData);
      setThemeRankingData(themeRanking);

      // 테마 자동 선택 제거 - 사용자가 직접 선택하도록
      // 기존 선택된 테마는 그대로 유지 (변경하지 않음)

      // rank 기준 오름차순 정렬 (원본 배열을 변경하지 않도록 복사 후 정렬)
      const sortedData = [...filteredData].sort((a, b) => {
        const rankA = parseInt(a.rank) || 0;
        const rankB = parseInt(b.rank) || 0;
        return rankA - rankB;
      });

      // 필터 적용 후 데이터가 없으면 표 비우기
      if (sortedData.length === 0) {
        console.log("필터 적용 후 데이터 없음 - 표 비우기");
      }

      // 원본 데이터 저장 (useEffect에서 필터링에 사용)
      setRawTradingData(sortedData);
    } catch (error) {
      console.error("거래대금 데이터 조회 실패:", error);
      // 에러 시 표 비우기
      setRawTradingData([]);
      setTradingData([]);
      setAllAvailableThemes([]);
      setThemeRankingData([]);
      alert(`데이터 조회에 실패했습니다: ${error.message || "네트워크 오류"}`);
    } finally {
      setIsSearching(false);
    }
  };

  // 숫자를 천단위 콤마로 포맷하는 함수 (소숫점 유지)
  const formatNumber = (value) => {
    if (value === 0 || value === null || value === undefined) return "0";
    return value.toLocaleString();
  };

  // 거래대금을 조, 억 단위로 포맷하는 함수
  const formatTradingValue = (value) => {
    if (value === 0 || value === null || value === undefined) return "0억";

    if (value >= 1000000000000) {
      const trillion = (value / 1000000000000).toFixed(2);
      return `${formatNumber(parseFloat(trillion))}조`;
    } else if (value >= 100000000) {
      const billion = (value / 100000000).toFixed(2);
      return `${formatNumber(parseFloat(billion))}억`;
    } else if (value >= 10000) {
      const thousand = (value / 10000).toFixed(0);
      return `${formatNumber(parseFloat(thousand))}만`;
    } else {
      return formatNumber(value);
    }
  };

  // 등락률을 포맷하는 함수
  const formatRate = (value) => {
    if (value === 0 || value === null || value === undefined) return "0.00%";
    const sign = value > 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}%`;
  };

  // 등락률 색상 클래스
  const getRateColorClass = (value) => {
    if (value > 0) return "rate-positive";
    if (value < 0) return "rate-negative";
    return "rate-neutral";
  };

  return (
    <div className="page-container">
      {/* 날짜/시간 선택 및 제어 패널 */}
      <div className="datetime-control-panel">
        <div className="datetime-inputs">
          <div className="input-group">
            <label>날짜</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className={`date-input ${
                !isDateAvailable(selectedDate) ? "invalid-date" : ""
              }`}
              title={
                !isDateAvailable(selectedDate)
                  ? "조회할 수 없는 날짜입니다"
                  : ""
              }
            />
          </div>

          <div className="input-group">
            <label>시간</label>
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => handleTimeChange(e.target.value)}
              min="09:00"
              max={
                isSelectedDateToday(selectedDate)
                  ? (() => {
                      const now = new Date();
                      const currentHour = now
                        .getHours()
                        .toString()
                        .padStart(2, "0");
                      const currentMinute = now
                        .getMinutes()
                        .toString()
                        .padStart(2, "0");
                      return `${currentHour}:${currentMinute}`;
                    })()
                  : "15:30"
              }
              className={`time-input ${
                !isTimeValid(selectedDate, selectedTime) ? "invalid-time" : ""
              }`}
              title={
                !isTimeValid(selectedDate, selectedTime)
                  ? isSelectedDateToday(selectedDate) &&
                    isTimeAfterNow(selectedTime)
                    ? "현재 시간까지만 선택 가능합니다"
                    : "09:00 ~ 15:30 시간대만 선택 가능합니다"
                  : ""
              }
            />
          </div>

          <div className="input-group">
            <label>단위</label>
            <select
              value={timeUnit}
              onChange={(e) => setTimeUnit(e.target.value)}
              className="unit-select"
            >
              <option value="분">분</option>
              <option value="시">시</option>
              <option value="일">일</option>
            </select>
          </div>
        </div>

        <div className="control-buttons">
          <button onClick={handlePrevious} className="nav-btn prev-btn">
            &#8249;
          </button>
          <button onClick={handleNext} className="nav-btn next-btn">
            &#8250;
          </button>
          <button
            onClick={handleSearch}
            className="search-btn"
            disabled={
              loading ||
              isSearching ||
              !isDateAvailable(selectedDate) ||
              !isTimeValid(selectedDate, selectedTime)
            }
          >
            {isSearching ? "조회 중..." : "조회"}
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="settings-btn"
            style={{
              background: "none",
              border: "none",
              fontSize: "18px",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "4px",
            }}
            title="설정"
          >
            ⚙️
          </button>
        </div>
      </div>

      {/* 테마 필터 설정 섹션 */}
      <div className="theme-filter-settings-section">
        <button
          className="theme-filter-toggle-btn"
          onClick={() => setShowThemeFilterSettings(!showThemeFilterSettings)}
        >
          {showThemeFilterSettings ? "▼" : "▶"} 테마 필터 설정
        </button>

        {showThemeFilterSettings && (
          <div className="theme-filter-settings-content">
            <div className="ignore-keywords-section">
              <h4>무시할 테마 키워드</h4>
              <p className="settings-description">
                여기에 등록한 키워드가 포함된 테마는 랭킹 계산 및 표시에서
                제외됩니다. (대소문자 구분 없음)
              </p>

              <div className="keyword-input-group">
                <input
                  type="text"
                  value={newIgnoreKeyword}
                  onChange={(e) => setNewIgnoreKeyword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAddIgnoreKeyword();
                    }
                  }}
                  placeholder="예: 밸류, 지수, ETF"
                  className="keyword-input"
                />
                <button
                  onClick={handleAddIgnoreKeyword}
                  className="add-keyword-btn"
                >
                  추가
                </button>
              </div>

              {ignoreThemeKeywords.length > 0 ? (
                <div className="keyword-list">
                  {ignoreThemeKeywords.map((keyword, index) => (
                    <div key={index} className="keyword-item">
                      <span className="keyword-text">{keyword}</span>
                      <button
                        onClick={() => handleRemoveIgnoreKeyword(keyword)}
                        className="remove-keyword-btn"
                        title="삭제"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-keywords-message">등록된 키워드가 없습니다.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 테마 랭킹 표 */}
      {themeRankingData.length > 0 && (
        <div className="theme-ranking-section">
          <div className="theme-ranking-header">
            <h3 className="theme-ranking-title">
              📊 테마별 거래대금 상위{" "}
              {currentThemePage === 0 ? "1-5위" : "6-10위"}
            </h3>
            {console.log("🔍 [Debug] 페이지네이션 상태:", {
              currentThemePage,
              themeRankingDataLength: themeRankingData.length,
              prevDisabled: currentThemePage === 0,
              nextDisabled:
                currentThemePage === 1 || themeRankingData.length <= 5,
            })}
            <div className="theme-pagination-controls">
              <button
                className="theme-prev-btn"
                onClick={(e) => {
                  console.log("🔍 [Debug] 이전 버튼 클릭됨!", e);
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentThemePage(0);
                }}
                disabled={currentThemePage === 0}
                title="1-5위"
                style={{
                  cursor: currentThemePage === 0 ? "default" : "pointer",
                  background: currentThemePage === 0 ? "#e9ecef" : "#ffffff",
                  opacity: currentThemePage === 0 ? 0.6 : 1,
                }}
              >
                ←
              </button>
              <button
                className="theme-next-btn"
                onClick={(e) => {
                  console.log("🔍 [Debug] 다음 버튼 클릭됨!", e);
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentThemePage(1);
                }}
                disabled={
                  currentThemePage === 1 || themeRankingData.length <= 5
                }
                title="6-10위"
                style={{
                  cursor:
                    currentThemePage === 1 || themeRankingData.length <= 5
                      ? "default"
                      : "pointer",
                  background: currentThemePage === 1 ? "#e9ecef" : "#ffffff",
                  opacity:
                    currentThemePage === 1 || themeRankingData.length <= 5
                      ? 0.6
                      : 1,
                }}
              >
                →
              </button>
            </div>
          </div>
          <div className="theme-ranking-table-container">
            <table className="theme-ranking-table">
              <thead>
                <tr>
                  <th>순위</th>
                  <th>테마명</th>
                  <th>거래대금 합</th>
                  <th>종목 수</th>
                  <th>평균 등락률</th>
                  <th>대장주</th>
                  <th>대장주 등락률</th>
                  <th>대장주 현재가</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const startIndex = currentThemePage * 5;
                  const endIndex = startIndex + 5;
                  const currentPageData = themeRankingData.slice(
                    startIndex,
                    endIndex
                  );

                  // 5개 행을 채우기 위해 빈 행 생성
                  const rows = [];

                  // 실제 데이터 행들
                  currentPageData.forEach((theme, index) => {
                    const actualIndex = startIndex + index;
                    rows.push(
                      <tr
                        key={theme.name}
                        className="clickable-theme-row"
                        onClick={() => handleThemeRankingClick(theme.name)}
                      >
                        <td className="rank-cell">
                          <span className="rank-number">{actualIndex + 1}</span>
                        </td>
                        <td className="theme-name-cell">
                          <span
                            className={`theme-name ${
                              selectedThemes.has(theme.name)
                                ? "selected-theme-name"
                                : ""
                            }`}
                          >
                            {theme.name}
                            {selectedThemes.has(theme.name) && (
                              <span className="selected-indicator"> ✓</span>
                            )}
                          </span>
                        </td>
                        <td className="trading-value-cell">
                          <span className="trading-value">
                            {formatTradingValue(theme.totalTradingValue)}
                          </span>
                        </td>
                        <td className="stock-count-cell">
                          <span className="stock-count">
                            {theme.stockCount}개
                          </span>
                        </td>
                        <td
                          className={`avg-rate-cell ${getRateColorClass(
                            theme.totalRate / theme.stockCount
                          )}`}
                        >
                          <span className="avg-rate">
                            {formatRate(theme.totalRate / theme.stockCount)}
                          </span>
                        </td>
                        <td className="leader-stock-cell">
                          <span className="leader-stock">
                            {theme.leaderStock}
                          </span>
                        </td>
                        <td
                          className={`leader-rate-cell ${getRateColorClass(
                            theme.leaderRate
                          )}`}
                        >
                          <span className="leader-rate">
                            {formatRate(theme.leaderRate)}
                          </span>
                        </td>
                        <td className="leader-price-cell">
                          <span className="leader-price">
                            {formatNumber(theme.leaderPrice)}
                          </span>
                        </td>
                      </tr>
                    );
                  });

                  // 빈 행들 추가 (5개 행을 맞추기 위해)
                  const emptyRowsCount = 5 - currentPageData.length;
                  for (let i = 0; i < emptyRowsCount; i++) {
                    rows.push(
                      <tr key={`empty-${i}`} className="empty-theme-row">
                        <td className="rank-cell">
                          <span className="rank-number">
                            {startIndex + currentPageData.length + i + 1}
                          </span>
                        </td>
                        <td className="theme-name-cell">-</td>
                        <td className="trading-value-cell">-</td>
                        <td className="stock-count-cell">-</td>
                        <td className="avg-rate-cell">-</td>
                        <td className="leader-stock-cell">-</td>
                        <td className="leader-rate-cell">-</td>
                        <td className="leader-price-cell">-</td>
                      </tr>
                    );
                  }

                  return rows;
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 테마 선택 라벨 섹션 */}
      {allAvailableThemes.length > 0 && (
        <div className="theme-selection-section">
          <div className="theme-selection-header">
            <h3 className="theme-selection-title">
              🏷️ 상위 15개 테마 선택 ({selectedThemes.size}개 선택됨)
            </h3>
            <button
              className="select-all-themes-btn"
              onClick={handleSelectAllThemes}
              title={
                selectedThemes.size === allAvailableThemes.length
                  ? "전체 해제"
                  : "전체 선택"
              }
            >
              {selectedThemes.size === allAvailableThemes.length
                ? "전체 해제"
                : "전체 선택"}
            </button>
          </div>
          <div className="theme-labels-container">
            {allAvailableThemes.map((themeName) => {
              const isSelected = selectedThemes.has(themeName);
              const isTopTheme = themeRankingData
                .slice(0, 5)
                .some((theme) => theme.name === themeName);

              return (
                <button
                  key={themeName}
                  className={`theme-label ${isSelected ? "selected" : ""} ${
                    isTopTheme ? "top-theme" : ""
                  }`}
                  onClick={() => handleThemeToggle(themeName)}
                  title={isTopTheme ? "상위 5개 테마" : ""}
                >
                  <span className="theme-name">{themeName}</span>
                  {isTopTheme && <span className="top-indicator">⭐</span>}
                </button>
              );
            })}
          </div>
          {selectedThemes.size === 0 && (
            <div className="no-theme-selected">
              <p>테마를 선택하면 해당 테마의 종목들이 표시됩니다.</p>
            </div>
          )}
        </div>
      )}

      <TradingTable
        key="theme-page-trading-table"
        pageId="ThemePage"
        data={tradingData}
        isLoading={isInitializing}
        isSearching={isSearching}
        showStarButton={true}
        loadingMessage="데이터를 조회하고 있습니다..."
        initialLoadingMessage="초기 데이터를 로딩하고 있습니다..."
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        visibleDetails={visibleDetails}
        setVisibleDetails={setVisibleDetails}
        chartSettings={chartSettings}
        setChartSettings={setChartSettings}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        selectedDate={selectedDate}
        showDetailsSection={showDetailsSection}
        setShowDetailsSection={handleSetShowDetailsSection}
        topThemes={allAvailableThemes}
      />

      <div className="last-updated">
        <span>마지막 업데이트: {new Date().toLocaleString()}</span>
      </div>
    </div>
  );
};

export default ThemePage;
